import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BillingModeUnique } from '@/components/Elements/BillingModeUnique';
import { CurrencySwitch } from '@/components/Elements/CurrencySwitch';
import { ProductCard } from '@/components/Elements/ProductCard';
import { Slider, SliderProps } from '@/components/Elements/Slider';
import { useMe } from '@/hooks/useMe';
import { useUserProducts } from '@/hooks/useUserProducts';
import {
  BillingMode,
  Currency,
  Me,
  ParsedSliderMarkType,
  ProductTypes,
  SliderMarkType,
  UserProductTypes,
} from '@/types';
import { getNoPlanProduct, shouldShowTheCurrencySwitch } from '@/utils/helpers';

export type PrepaidMarketingContainerProps = {
  title: string;
  products: ProductTypes[][];
  billingMode?: BillingMode;
  onProductSelect: (product: ProductTypes) => void;
  onRequestInfo: (product: ProductTypes) => void;
  userProducts: UserProductTypes[] | undefined;
  userIsLogged?: boolean;
} & SliderProps;

export const PrepaidMarketingContainer = ({
  title,
  products,
  min = 0,
  max,
  onSliderChange,
  onProductSelect,
  onRequestInfo,
  defaultValue = 0,
  marks = {},
  billingMode = 'unique',
  userProducts,
  userIsLogged,
}: PrepaidMarketingContainerProps) => {
  const { getMe } = useMe();
  const me = getMe();

  const step = 1;
  const { t } = useTranslation();
  const { setSelectedProductCurrency, getSelectedUserProduct } = useUserProducts();
  const [productsCollection, setProductsCollection] = useState(getNoPlanProduct(products, marks));
  const [currentStep, setCurrentStep] = useState(0);
  const { selectedProductCurrency } = getSelectedUserProduct();
  const parseMarks = (marks: SliderMarkType) => {
    const keys = Object.keys(marks);
    const result: ParsedSliderMarkType = {};

    keys.forEach((key: string) => {
      const iKey = +key;
      result[iKey] = (
        <div className="min-w-[100px]">
          <span className="hidden lg:block font-bold block text-black uppercase">
            {iKey === keys.length - 1 ? '>' : ''}
            {t(`productSteps.${marks[iKey]}`)}
          </span>
          <span className="hidden lg:block block text-black">{t(`common.emails`)}</span>
        </div>
      );
    });

    return result;
  };

  const marksParsed = parseMarks(marks);

  const unselectStep = () => {
    const currentSelectedStepLabel = document.querySelector('.step--selected');
    currentSelectedStepLabel && currentSelectedStepLabel.classList.remove('step--selected');
  };

  const selectStep = (step: number = currentStep + 1) => {
    unselectStep();

    const currentStepLabel = document.querySelector(
      `.rc-slider-mark-text:nth-of-type(${step}) > div`
    );

    currentStepLabel && currentStepLabel.classList.add('step--selected');
  };

  useEffect(() => {
    selectStep();
  });

  const onDefaultSliderChange = (value: number) => {
    setCurrentStep(value);
    setProductsCollection(getNoPlanProduct(products, marks, value));

    selectStep(value);

    onSliderChange && onSliderChange(value);
  };

  const onChangeCurrencySwitch = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSelectedProductCurrency(event.target.value as Currency);

  const getActionButtonLabel = (me: Me) => {
    if (userIsLogged && !me.capabilities.canBuyProducts) {
      return t('ProductDetail.writeUs');
    }

    return t('ProductDetail.payNow');
  };

  const getOnActionButton = (me: Me) => {
    if (userIsLogged && !me.capabilities.canBuyProducts) {
      return onRequestInfo;
    }

    return undefined;
  };

  return (
    <section className="bg-md-yellow">
      <div className="max-w-6xl m-auto px-3 py-2 lg:px-8 lg:pt-10 lg:pb-5">
        <h2 className="md__payments__title--h2">{title}</h2>
        <div className="flex justify-center lg:justify-between flex-wrap lg:flex-nowrap">
          <div
            className={clsx('mr-0 lg:mr-20 w-full relative', {
              'lg:w-11/12': !shouldShowTheCurrencySwitch(userProducts),
              'lg:w-7/12': shouldShowTheCurrencySwitch(userProducts),
            })}
          >
            <Slider
              min={min}
              max={max}
              step={step}
              onSliderChange={onDefaultSliderChange}
              defaultValue={defaultValue}
              marks={marksParsed}
              selectMarks={marks}
              selectClass="mb-5 lg:mb-0"
            />
          </div>
          <div className="mr-0 mt-4 lg:mt-[-70px] flex justify-center lg:justify-between items-end flex-wrap z-[5]">
            <BillingModeUnique
              className={clsx({
                'mt-6 mr-0': !shouldShowTheCurrencySwitch(userProducts),
              })}
            />
            {shouldShowTheCurrencySwitch(userProducts) && (
              <div className="w-[150px] flex justify-center items-center flex-wrap">
                <CurrencySwitch
                  defaultValue={selectedProductCurrency}
                  onChange={onChangeCurrencySwitch}
                  className="ml-8"
                  title={t('common.currency.title')}
                />
              </div>
            )}
          </div>
        </div>
        <div>
          <div className="flex flex-col-reverse lg:flex-row flex-wrap lg:flex-nowrap mt-2 lg:mt-12 text-black pb-10 gap-6">
            <div className="w-full lg:w-2/3">
              <div className="text-center lg:text-left">
                <h3 className="md__payments__container--h3">{t('productTypes.prepaid.prices')}</h3>
                <p className="text-md lg:text-xl">{t('productTypes.prepaid.productDescription')}</p>
                <h3 className="md__payments__container--h3">
                  {t('productTypes.prepaid.needMore')}
                </h3>
                <p className="text-md lg:text-xl">{t('productTypes.prepaid.needMoreText')}</p>
              </div>
            </div>
            <div className="w-full lg:w-1/3">
              <ProductCard
                actionButtonLabel={getActionButtonLabel(me)}
                onActionButton={getOnActionButton(me)}
                onRequestInfo={onRequestInfo}
                onProductSelect={onProductSelect}
                product={productsCollection}
                userCurrency={selectedProductCurrency}
                userBillingMode={billingMode}
                className="w-full pt-8 px-1 pb-1"
              />
            </div>
          </div>
          <p className="text-right italic text-xs mt-7">{t('ProductDetail.taxesDisclaimer')}</p>
        </div>
      </div>
    </section>
  );
};
